import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { CurrencyRailProvider } from "@/context/CurrencyRailContext";
import { EntityProvider, useEntity } from "@/context/EntityContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import PersonalDashboard from "@/pages/PersonalDashboard";
import Transactions from "@/pages/Transactions";
import Analytics from "@/pages/Analytics";
import Budgets from "@/pages/Budgets";
import Accounts from "@/pages/Accounts";
import Recurring from "@/pages/Recurring";
import Banks from "@/pages/Banks";
import Reports from "@/pages/Reports";
import Notifications from "@/pages/Notifications";
import Goals from "@/pages/Goals";
import Investments from "@/pages/Investments";
import FixedAssets from "@/pages/FixedAssets";
import Transfers from "@/pages/Transfers";
import Hub from "@/pages/Hub";
import MemosPage from "@/pages/MemosPage";
import PublicInvoicePay from "@/pages/PublicInvoicePay";
import ReceivePayment from "@/pages/ReceivePayment";
import PayBills from "@/pages/PayBills";
import CashRegister from "@/pages/CashRegister";
import Settings from "@/pages/Settings";
import Categories from "@/pages/Categories";
import Accounting from "@/pages/Accounting";
import Entities from "@/pages/Entities";
import Diagnostics from "@/pages/Diagnostics";
import Rules from "@/pages/Rules";
import ChartOfAccounts from "@/pages/ChartOfAccounts";
import Receivables from "@/pages/Receivables";
import Payables from "@/pages/Payables";
import ManagementDashboard from "@/pages/ManagementDashboard";
import Financials from "@/pages/Financials";
import Team from "@/pages/Team";
import AcceptInvite from "@/pages/AcceptInvite";
import Inventory from "@/pages/Inventory";
import Payroll from "@/pages/Payroll";
import AIHub from "@/pages/AIHub";
import ReportDesigner from "@/pages/ReportDesigner";
import OfflineIndicator from "@/components/OfflineIndicator";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import WorkspacePicker from "@/pages/WorkspacePicker";
import PersonalReports from "@/pages/PersonalReports";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-[var(--muted-text)]">
        Loading...
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return (
    <CurrencyProvider primary={user.currency || "USD"}>
      <CurrencyRailProvider>
        <EntityProvider>
          <WorkspaceGate>{children}</WorkspaceGate>
        </EntityProvider>
      </CurrencyRailProvider>
    </CurrencyProvider>
  );
}

// iter 72 — if the user hasn't chosen Personal vs Business for this session,
// send them to the picker. Any explicit route (like /workspace itself) is
// exempted so the picker can render without an infinite loop.
function WorkspaceGate({ children }) {
  const { workspaceKind, loading } = useEntity();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-[var(--muted-text)]">
        Loading...
      </div>
    );
  }
  if (!workspaceKind && window.location.pathname !== "/workspace") {
    return <Navigate to="/workspace" replace />;
  }
  return children;
}

// iter 73 — Route the /dashboard path to the Personal or Business dashboard
// based on the active entity's workspace_type. `workspaceKind` (from Entity
// context) is the session-cached user pick and always wins; we fall back to
// the entity's stored `workspace_type` so a page refresh mid-session still
// lands on the right dashboard.
function DashboardByKind() {
  const { workspaceKind, currentEntity } = useEntity();
  const kind = workspaceKind || currentEntity?.workspace_type;
  if (kind === "personal") return <PersonalDashboard />;
  return <Dashboard />;
}

// Same tailoring for the "/" hub landing — Personal users skip the business
// Hub (which shows AR/AP tiles) and go straight to their calm dashboard.
function HubByKind() {
  const { workspaceKind, currentEntity } = useEntity();
  const kind = workspaceKind || currentEntity?.workspace_type;
  if (kind === "personal") return <PersonalDashboard />;
  return <Hub />;
}

// The workspace picker route is Protected (need auth) but NOT gated (would
// loop). We embed only the contexts it needs, without WorkspaceGate.
function ProtectedForPicker({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-[var(--muted-text)]">
        Loading...
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return (
    <CurrencyProvider primary={user.currency || "USD"}>
      <CurrencyRailProvider>
        <EntityProvider>{children}</EntityProvider>
      </CurrencyRailProvider>
    </CurrencyProvider>
  );
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
              <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
              <Route path="/accept-invite" element={<AcceptInvite />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/" element={<Protected><HubByKind /></Protected>} />
              <Route path="/workspace" element={<ProtectedForPicker><WorkspacePicker /></ProtectedForPicker>} />
              <Route path="/dashboard" element={<Protected><DashboardByKind /></Protected>} />
              <Route path="/hub" element={<Protected><Hub /></Protected>} />
              <Route path="/customer-memos" element={<Protected><MemosPage mode="customer" /></Protected>} />
              <Route path="/vendor-memos" element={<Protected><MemosPage mode="vendor" /></Protected>} />
              <Route path="/transactions" element={<Protected><Transactions /></Protected>} />
              <Route path="/transfers" element={<Protected><Transfers /></Protected>} />
              <Route path="/analytics" element={<Protected><Analytics /></Protected>} />
              <Route path="/budgets" element={<Protected><Budgets /></Protected>} />
              <Route path="/accounts" element={<Protected><Accounts /></Protected>} />
              <Route path="/recurring" element={<Protected><Recurring /></Protected>} />
              <Route path="/banks" element={<Protected><Banks /></Protected>} />
              <Route path="/reports" element={<Protected><Reports /></Protected>} />
              <Route path="/personal/reports" element={<Protected><PersonalReports /></Protected>} />
              <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
              <Route path="/goals" element={<Protected><Goals /></Protected>} />
              <Route path="/investments" element={<Protected><Investments /></Protected>} />
              <Route path="/fixed-assets" element={<Protected><FixedAssets /></Protected>} />
              <Route path="/settings" element={<Protected><Settings /></Protected>} />
              <Route path="/categories" element={<Protected><Categories /></Protected>} />
              <Route path="/accounting" element={<Protected><Accounting /></Protected>} />
              <Route path="/entities" element={<Protected><Entities /></Protected>} />
              <Route path="/admin/diagnostics" element={<Protected><Diagnostics /></Protected>} />
              <Route path="/rules" element={<Protected><Rules /></Protected>} />
              <Route path="/chart-of-accounts" element={<Protected><ChartOfAccounts /></Protected>} />
              <Route path="/receivables" element={<Protected><Receivables /></Protected>} />
              <Route path="/payables" element={<Protected><Payables /></Protected>} />
              <Route path="/executive" element={<Protected><ManagementDashboard /></Protected>} />
              <Route path="/financials" element={<Protected><Financials /></Protected>} />
              <Route path="/settings/team" element={<Protected><Team /></Protected>} />
              <Route path="/team" element={<Protected><Team /></Protected>} />
              <Route path="/inventory" element={<Protected><Inventory /></Protected>} />
              <Route path="/payroll" element={<Protected><Payroll /></Protected>} />
              <Route path="/ai" element={<Protected><AIHub /></Protected>} />
              <Route path="/reports/designer" element={<Protected><ReportDesigner /></Protected>} />
              <Route path="/receive-payment" element={<Protected><ReceivePayment /></Protected>} />
              <Route path="/pay-bills" element={<Protected><PayBills /></Protected>} />
              <Route path="/reports/cash-register" element={<Protected><CashRegister /></Protected>} />
              <Route path="/pay/:token" element={<PublicInvoicePay />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster />
            <OfflineIndicator />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}

export default App;
